import { Colors, ConfirmDialog, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { Pillar } from '@/types/pillars';

import { PillarStatus } from '@/constants/PillarStatus';
import { getLocalizedText } from '@/utils/localization';

const WORDING = {
    [PillarStatus.INACTIVE]: {
        title: 'pillars.deactivateTitle',
        message: 'pillars.deactivateMessage',
        confirm: 'pillars.deactivate',
        impactCount: 'pillars.deactivateImpactCount',
        impactNone: 'pillars.deactivateImpactNone',
    },
    [PillarStatus.DRAFT]: {
        title: 'pillars.moveToDraftTitle',
        message: 'pillars.moveToDraftMessage',
        confirm: 'pillars.moveToDraft',
        impactCount: 'pillars.moveToDraftImpactCount',
        impactNone: 'pillars.moveToDraftImpactNone',
    },
} as const;

interface PillarLeaveActiveDialogProps {
    isOpen: boolean;
    pillar: Pillar | null;
    /** The status the pillar is leaving Active for. */
    targetStatus: typeof PillarStatus.INACTIVE | typeof PillarStatus.DRAFT;
    isConfirming: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * Warns before a pillar leaves Active, for Inactive or back to Draft: its objectives keep it, but it
 * is no longer offered for new ones. The caller owns the actual status change -- this dialog is shared
 * by the card's quick "Désactiver" action and the modal's status field, whose mutations differ.
 */
export default function PillarLeaveActiveDialog({
    isOpen,
    pillar,
    targetStatus,
    isConfirming,
    onCancel,
    onConfirm,
}: PillarLeaveActiveDialogProps) {
    const { t } = useTranslation();
    const wording = WORDING[targetStatus];

    return (
        <ConfirmDialog
            isOpen={isOpen}
            title={t(wording.title)}
            message={
                <div className={'flex flex-col gap-4'}>
                    <Text>{t(wording.message, { name: getLocalizedText(pillar?.name) })}</Text>

                    <Text color={Colors.BASE_600}>
                        {pillar?.linkedObjectivesCount
                            ? t(wording.impactCount, { count: pillar.linkedObjectivesCount })
                            : t(wording.impactNone)}
                    </Text>
                </div>
            }
            confirmLabel={t(wording.confirm)}
            cancelLabel={t('pillars.cancel')}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isLoading={isConfirming}
            dataTestId={'deactivate-pillar-dialog'}
        />
    );
}
